import { useState, useCallback, useEffect, useRef } from 'react';
import { getPublicEnv } from '../lib/publicEnv';
import { supabase } from '../lib/supabase';

type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

interface UsePushNotificationsReturn {
  permission: PushPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  registerSW: () => Promise<void>;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const array = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) array[i] = raw.charCodeAt(i);
  return array;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const [permission, setPermission] = useState<PushPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const swRegistration = useRef<ServiceWorkerRegistration | null>(null);

  // Check if push is supported
  const isSupported = 'serviceWorker' in navigator && 'PushManager' in window;

  const resolveRegistration = useCallback(async (createIfMissing = true) => {
    if (!isSupported) return null;

    if (swRegistration.current) {
      return swRegistration.current;
    }

    const existingRegistration =
      await navigator.serviceWorker.getRegistration()
      || await navigator.serviceWorker.getRegistration('/');

    if (existingRegistration) {
      swRegistration.current = existingRegistration;
      return existingRegistration;
    }

    if (!createIfMissing) {
      return null;
    }

    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    const readyRegistration = await navigator.serviceWorker.ready.catch(() => registration);
    swRegistration.current = readyRegistration;
    return readyRegistration;
  }, [isSupported]);

  const syncStatus = useCallback(async () => {
    if (!isSupported) {
      setPermission('unsupported');
      setIsSubscribed(false);
      return false;
    }

    setPermission(Notification.permission as PushPermission);

    try {
      const reg = await resolveRegistration(false);
      if (!reg) {
        setIsSubscribed(false);
        return false;
      }

      const sub = await reg.pushManager.getSubscription();
      const subscribed = !!sub;
      setIsSubscribed(subscribed);
      return subscribed;
    } catch {
      setIsSubscribed(false);
      return false;
    }
  }, [isSupported, resolveRegistration]);

  useEffect(() => {
    void syncStatus();
  }, [syncStatus]);

  useEffect(() => {
    if (!isSupported) return;

    const resync = () => {
      void syncStatus();
    };

    window.addEventListener('focus', resync);
    document.addEventListener('visibilitychange', resync);

    return () => {
      window.removeEventListener('focus', resync);
      document.removeEventListener('visibilitychange', resync);
    };
  }, [isSupported, syncStatus]);

  const registerSW = useCallback(async () => {
    if (!isSupported) return;

    try {
      const reg = await resolveRegistration(true);
      if (!reg) return;

      console.log('Service Worker registered:', reg.scope);
      await syncStatus();
    } catch (err) {
      console.error('SW registration failed:', err);
    }
  }, [isSupported, resolveRegistration, syncStatus]);

  const subscribe = useCallback(async () => {
    if (!isSupported) return false;
    setIsLoading(true);

    try {
      // Request notification permission
      const result = await Notification.requestPermission();
      setPermission(result as PushPermission);

      if (result !== 'granted') {
        setIsSubscribed(false);
        return false;
      }

      // Ensure SW is registered
      if (!swRegistration.current) {
        await registerSW();
      }

      const reg = await resolveRegistration(true);
      if (!reg) {
        return false;
      }

      const vapidKey = getPublicEnv('VITE_VAPID_PUBLIC_KEY');
      if (!vapidKey) {
        console.warn('VAPID public key not set');
        return false;
      }

      const existingSubscription = await reg.pushManager.getSubscription();
      const subscription = existingSubscription || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
      });

      setIsSubscribed(true);

      // Send to server
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ subscription: subscription.toJSON() }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Push subscription sync failed' }));
        throw new Error(error.error || 'Push subscription sync failed');
      }

      await syncStatus();
      return true;
    } catch (err) {
      console.error('Push subscription failed:', err);
      await syncStatus();
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, registerSW, syncStatus]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return false;
    setIsLoading(true);

    try {
      const reg = await resolveRegistration(false);
      if (!reg) {
        setIsSubscribed(false);
        return true;
      }

      const subscription = await reg.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from push
        await subscription.unsubscribe();

        // Remove from server
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch('/api/push/unsubscribe', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ subscription: subscription.toJSON() }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Push unsubscribe sync failed' }));
          throw new Error(error.error || 'Push unsubscribe sync failed');
        }
      }

      setIsSubscribed(false);
      await syncStatus();
      return true;
    } catch (err) {
      console.error('Push unsubscribe failed:', err);
      await syncStatus();
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, resolveRegistration, syncStatus]);

  return { permission, isSubscribed, isLoading, subscribe, unsubscribe, registerSW };
}
